import { ChevronDown, Pin } from 'lucide-react';
import type { ChatSession } from '../../../types/chatSession';
import type { SessionItemCallbacks } from './chatSessionItemTypes';
import { ChatSessionItem } from './ChatSessionItem';
import { ChatSessionListLoadingItem } from './ChatSessionListLoadingItem';

type PinnedChatSessionsGroupProps = SessionItemCallbacks & {
  sessions: ChatSession[];
  isExpanded: boolean;
  onToggle: () => void;
  expandLabel: string;
  collapseLabel: string;
  title: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
};

export function PinnedChatSessionsGroup({
  sessions,
  isExpanded,
  onToggle,
  expandLabel,
  collapseLabel,
  title,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  ...itemProps
}: PinnedChatSessionsGroupProps) {
  const panelId = 'liks-chat-sessions-pinned';
  const showLoadingRow = isExpanded && (hasNextPage || isFetchingNextPage);

  return (
    <section className="liks-chat-sessions__group liks-chat-sessions__group--pinned">
      <button
        type="button"
        className="liks-chat-sessions__group-toggle"
        aria-expanded={isExpanded}
        aria-controls={panelId}
        aria-label={isExpanded ? collapseLabel : expandLabel}
        onClick={onToggle}
      >
        <span className="liks-chat-sessions__group-heading">
          <Pin
            className="liks-chat-sessions__group-icon"
            strokeWidth={2}
            aria-hidden
          />
          <span className="liks-chat-sessions__group-title">{title}</span>
          <span className="liks-chat-sessions__group-count" aria-hidden>
            {sessions.length}
            {hasNextPage ? '+' : ''}
          </span>
        </span>
        <ChevronDown
          className={[
            'liks-chat-sessions__group-chevron',
            isExpanded && 'liks-chat-sessions__group-chevron--expanded',
          ]
            .filter(Boolean)
            .join(' ')}
          strokeWidth={2}
          aria-hidden
        />
      </button>

      {isExpanded && (
        <ul id={panelId} className="liks-chat-sessions__list">
          {sessions.map((session) => (
            <ChatSessionItem
              key={session.id}
              session={session}
              {...itemProps}
            />
          ))}
          {showLoadingRow && (
            <ChatSessionListLoadingItem
              enabled={hasNextPage && !isFetchingNextPage}
              isFetching={isFetchingNextPage}
              onVisible={onLoadMore}
            />
          )}
        </ul>
      )}
    </section>
  );
}
